/**
 * What a list page should look like when you come back to it.
 *
 * Filters, sort and column choices are a working position, not a setting: an
 * operator narrows a 9,000-product catalogue to one brand, sorts by quantity,
 * opens a product to fix it, comes back — and doing that work again on every
 * return is the difference between a tool and a chore.
 *
 * Kept PER USER, not just per tenant. A shop's office machine is shared: the
 * owner, the accountant and whoever is covering the counter all sign in to the
 * same browser, and inheriting someone else's filters is worse than none —
 * "where did my products go" is a support call, and the answer (a filter three
 * screens away, set by someone else yesterday) is not one anybody guesses.
 *
 * localStorage, deliberately: the auth session lives in sessionStorage and dies
 * with the tab, but a working position should outlive that. Nothing here is
 * secret — a category id and a sort direction — and it is namespaced by user so
 * signing in as someone else cannot show you theirs.
 */

/** `erp-<view>:<tenant>:<user>` — both scopes, so neither can leak into the other. */
export function viewPrefsKey(view: string, tenantId: string | null, userId: string | null): string {
  return `erp-${view}:${tenantId ?? 'x'}:${userId ?? 'x'}`;
}

/**
 * Read a view's saved state.
 *
 * `legacyKey` is the tenant-only key these preferences used before they were
 * scoped to a user: read once, so nobody's current filters vanish the day this
 * ships, and never written again.
 */
export function loadViewPrefs<T>(key: string, fallback: T, legacyKey?: string): T {
  for (const k of legacyKey ? [key, legacyKey] : [key]) {
    try {
      const raw = window.localStorage.getItem(k);
      if (raw) return { ...fallback, ...(JSON.parse(raw) as T) };
    } catch {
      /* unreadable or not ours — fall through to the next key, then the default */
    }
  }
  return fallback;
}

export function saveViewPrefs(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — a lost filter is not worth an error */
  }
}

/** The shape every list page persists. Pages add their own filters on top. */
export interface SortPref {
  id: string;
  dir: 'asc' | 'desc';
}
